<template>
    <v-container fluid class="mb-12 mb-lg-0">
        <v-row justify="center">
            <v-col cols="12">
                <v-card width="100%">
                    <v-card-title class="d-block py-2 text-center font-weight-bold primary secondary--text" style="word-break: normal">
                        CUENTAS BANCARIAS DE CLIENTES
                    </v-card-title>
                    <v-btn bottom color="secondary primary--text" depressed fab absolute right small @click="getAccounts(true)">
                        <v-icon>mdi-sync</v-icon>
                    </v-btn>
                </v-card>
            </v-col>
        </v-row>

        <v-row justify="center">
            <v-col cols="12">
                <v-card>
                    <v-card-title class="align-center flex-wrap">
                        <v-text-field
                            v-model="search"
                            append-icon="mdi-magnify"
                            label="Buscar"
                            single-line
                            hide-details
                            class="mr-md-4"/>
                        <v-spacer/>
                        <v-select
                            v-model="status"
                            :items="statuses"
                            item-text="text"
                            item-value="value"
                            label="Estado"
                            dense
                            outlined
                            hide-details
                            style="max-width: 220px"
                            @change="getAccounts(true)"/>
                    </v-card-title>
                    <v-data-table
                        :headers="headers"
                        :items="accounts"
                        :search="search"
                        :loading="loading"
                        :no-data-text="noDataText"
                        :footer-props="footerProps">
                        <template v-slot:item.customer="{ item }">
                            <div class="font-weight-medium" style="text-transform: capitalize">
                                {{ item.customer ? item.customer.toLowerCase() : '' }}
                            </div>
                            <div class="caption primary--text font-weight-bold">{{ item.nickname }}</div>
                        </template>
                        <template v-slot:item.bank_name="{ item }">
                            <strong>{{ item.bank_name }}</strong>
                            <div class="caption">{{ item.account_type }} {{ item.account_mask }}</div>
                        </template>
                        <template v-slot:item.status_label="{ item }">
                            <v-chip small dark :color="statusColor(item.status)">
                                {{ item.status_label }}
                            </v-chip>
                        </template>
                        <template v-slot:item.certificate_url="{ item }">
                            <v-btn icon :href="item.certificate_url" target="_blank">
                                <v-icon>mdi-file-eye</v-icon>
                            </v-btn>
                        </template>
                        <template v-slot:item.action="{ item }">
                            <v-btn
                                color="success"
                                small
                                depressed
                                dark
                                :disabled="item.status !== 'pending' || loading"
                                @click="openApprove(item)"
                                style="margin: 1px">
                                <v-icon>mdi-check-circle</v-icon>
                            </v-btn>
                            <v-btn
                                color="error"
                                small
                                depressed
                                dark
                                :disabled="item.status !== 'pending' || loading"
                                @click="openReject(item)"
                                style="margin: 1px">
                                <v-icon>mdi-close-circle</v-icon>
                            </v-btn>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>

        <v-dialog v-model="approveDialog" persistent max-width="560px">
            <v-card>
                <v-card-title class="primary secondary--text d-block text-center" style="word-break: normal">
                    Aprobar cuenta bancaria
                </v-card-title>
                <v-card-text class="pt-4" v-if="selected">
                    <v-list dense>
                        <v-list-item>
                            <v-list-item-content>
                                <v-list-item-title>Cliente</v-list-item-title>
                                <v-list-item-subtitle>{{ selected.customer }} - {{ selected.nickname }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <v-list-item-title>Titular</v-list-item-title>
                                <v-list-item-subtitle>{{ selected.holder_name }} - {{ selected.holder_document }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <v-list-item-title>Cuenta</v-list-item-title>
                                <v-list-item-subtitle>{{ selected.bank_name }} - {{ selected.account_type }} {{ selected.account_mask }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-btn color="error" text @click="closeDialogs" :disabled="loading">
                        <v-icon>mdi-close-circle</v-icon>&nbsp;Cerrar
                    </v-btn>
                    <v-spacer/>
                    <v-btn color="success" :loading="loading" :disabled="loading" @click="submitApprove">
                        <v-icon>mdi-check-circle</v-icon>&nbsp;Aprobar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="rejectDialog" persistent max-width="560px">
            <v-card>
                <v-card-title class="primary secondary--text d-block text-center" style="word-break: normal">
                    Rechazar cuenta bancaria
                </v-card-title>
                <v-card-text class="pt-4" v-if="selected">
                    <p class="mb-1"><strong>{{ selected.customer }} - {{ selected.nickname }}</strong></p>
                    <p class="mb-3">{{ selected.bank_name }} - {{ selected.account_type }} {{ selected.account_mask }}</p>
                    <v-textarea
                        v-model="reason"
                        label="Motivo"
                        rows="3"
                        :disabled="loading"/>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-btn color="error" text @click="closeDialogs" :disabled="loading">
                        <v-icon>mdi-close-circle</v-icon>&nbsp;Cerrar
                    </v-btn>
                    <v-spacer/>
                    <v-btn color="error" :loading="loading" :disabled="loading" @click="submitReject">
                        <v-icon>mdi-close-circle</v-icon>&nbsp;Rechazar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import axios from 'axios';

export default {
    name: "CustomerBankAccounts",
    props: ['user'],
    data: () => ({
        search: '',
        status: 'pending',
        statuses: [
            { text: 'Pendientes', value: 'pending' },
            { text: 'Aprobadas', value: 'approved' },
            { text: 'Rechazadas', value: 'rejected' },
            { text: 'Todas', value: 'all' }
        ],
        headers: [
            { text: 'Cliente', value: 'customer' },
            { text: 'Correo', value: 'email' },
            { text: 'Banco', value: 'bank_name' },
            { text: 'Titular', value: 'holder_name' },
            { text: 'Estado', value: 'status_label', align: 'center' },
            { text: 'Certificado', value: 'certificate_url', align: 'center', sortable: false },
            { text: 'Nota', value: 'review_note' },
            { text: 'Creada', value: 'created_at' },
            { text: 'Accion', value: 'action', align: 'center', filterable: false, sortable: false }
        ],
        accounts: [],
        loading: true,
        approveDialog: false,
        rejectDialog: false,
        selected: null,
        reason: '',
        noDataText: 'No hay cuentas bancarias para mostrar',
        footerProps: {
            itemsPerPageOptions: [25, 50, 100, 250, -1]
        }
    }),
    created() {
        this.getAccounts();
    },
    methods: {
        getAccounts(reset) {
            this.loading = true;
            if (reset === true) {
                this.accounts = [];
            }

            axios.get('/api/admin/customer-bank-accounts', { params: { status: this.status } }).then(res => {
                this.accounts = res.data.accounts || [];
            }).catch(err => {
                this.$toast.error(this.errorMessage(err, 'No fue posible cargar las cuentas.'), { icon: 'mdi-alert-circle' });
            }).then(() => {
                this.loading = false;
            });
        },
        openApprove(item) {
            this.selected = item;
            this.approveDialog = true;
        },
        openReject(item) {
            this.selected = item;
            this.reason = '';
            this.rejectDialog = true;
        },
        closeDialogs() {
            this.approveDialog = false;
            this.rejectDialog = false;
            this.selected = null;
            this.reason = '';
        },
        submitApprove() {
            if (!this.selected) return;

            this.loading = true;
            axios.post('/api/admin/customer-bank-accounts/' + this.selected.id + '/approve').then(res => {
                this.$toast.success(res.data.status, { icon: 'mdi-check-circle' });
                this.closeDialogs();
                this.getAccounts(true);
            }).catch(err => {
                this.$toast.error(this.errorMessage(err, 'No fue posible aprobar la cuenta.'), { icon: 'mdi-alert-circle' });
            }).then(() => {
                this.loading = false;
            });
        },
        submitReject() {
            if (!this.selected) return;

            this.loading = true;
            axios.post('/api/admin/customer-bank-accounts/' + this.selected.id + '/reject', {
                reason: this.reason
            }).then(res => {
                this.$toast.success(res.data.status, { icon: 'mdi-check-circle' });
                this.closeDialogs();
                this.getAccounts(true);
            }).catch(err => {
                this.$toast.error(this.errorMessage(err, 'No fue posible rechazar la cuenta.'), { icon: 'mdi-alert-circle' });
            }).then(() => {
                this.loading = false;
            });
        },
        statusColor(status) {
            if (status === 'approved') return 'success';
            if (status === 'rejected') return 'error';
            return 'warning';
        },
        errorMessage(err, fallback) {
            if (err.response && err.response.data && err.response.data.status) {
                return err.response.data.status;
            }

            return fallback;
        }
    }
}
</script>
